import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { checkAndRefreshTokenIfNeeded, logout } from "./api/Auth";
import { UserProfile } from './types/User';
import { useAnalysisOptIn } from './components/modals/AnalysisOptInProvider';

export interface AuthState {
    isAuthenticated: boolean;
    userProfile: UserProfile;
}

export const useAuth = () => {
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticated: false,
        userProfile: {
            email: null,
            firstName: null,
            lastName: null,
            profileUrl: null,
            sub: null,
        }
    });

    const [loading, setLoading] = useState(true);
    const [lastCheckTime, setLastCheckTime] = useState(0);
    const { showOptInModal } = useAnalysisOptIn();

    // Fix 1: Use a separate useEffect to log state changes properly
    useEffect(() => {
        if (authState.isAuthenticated) {
            console.log('Updated authState:', authState);
        }
    }, [authState]);

    const setUserProfileFromSession = useCallback(() => {
        const userProfile = {
            email: sessionStorage.getItem('email'),
            firstName: sessionStorage.getItem('firstName'),
            lastName: sessionStorage.getItem('lastName'),
            profileUrl: sessionStorage.getItem('profileUrl'),
            sub: localStorage.getItem('sub'),
            uploadCount: Number(sessionStorage.getItem('uploadCount') || '0'),
            uploadLimit: Number(sessionStorage.getItem('uploadLimit') || '0'),
            downloadCount: Number(sessionStorage.getItem('downloadCount') || '0'),
            downloadLimit: Number(sessionStorage.getItem('downloadLimit') || '0'),
            enableFaceAnalysis: sessionStorage.getItem('enableFaceAnalysis') === 'true',
            enableImageAnalysis: sessionStorage.getItem('enableImageAnalysis') === 'true'
        };

        setAuthState({
            isAuthenticated: true,
            userProfile
        });
        
        // Check if user has opted out of face or image analysis
        // Default for enableFaceAnalysis is false, default for enableImageAnalysis is true
        const faceAnalysisDisabled = userProfile.enableFaceAnalysis === false;
        const imageAnalysisDisabled = userProfile.enableImageAnalysis === false;
        
        if (faceAnalysisDisabled || imageAnalysisDisabled) {
            // Show the opt-in modal
            setTimeout(() => showOptInModal(), 500); // Slight delay to ensure UI is ready
        }
    }, [showOptInModal]);

    const updateUserCounters = useCallback((updates: {
        uploadCount?: number;
        downloadCount?: number;
        uploadLimit?: number;
        downloadLimit?: number;
    }) => {
        // Update session storage with new values
        Object.entries(updates).forEach(([key, value]) => {
            if (value !== undefined) {
                sessionStorage.setItem(key, value.toString());
            }
        });

        // Update the auth state with new values
        setAuthState(prevState => ({
            ...prevState,
            userProfile: {
                ...prevState.userProfile,
                ...updates
            }
        }));
    }, []);

    const checkAuthStatus = useCallback(async () => {
        const now = Date.now();
        // Prevent checking more than once every 5 seconds
        if (now - lastCheckTime < 5000) {
            return authState.isAuthenticated;
        }
        setLastCheckTime(now);

        const token = sessionStorage.getItem('token');
        const sub = localStorage.getItem('sub');

        // If we have both token and sub, just validate the token
        if (token && sub) {
            try {
                const tokenCheck = await checkAndRefreshTokenIfNeeded();
                if (tokenCheck.isValid) {
                    if (!authState.isAuthenticated) {
                        setUserProfileFromSession();
                    }
                    setLoading(false);
                    return true;
                }
            } catch (error) {
                console.error("Error checking token:", error);
            }
        }

        // If no token/sub or invalid token, clear auth state
        setAuthState({
            isAuthenticated: false,
            userProfile: {
                email: null,
                firstName: null,
                lastName: null,
                profileUrl: null,
                sub: null,
            }
        });
        setLoading(false);
        return false;
    }, [lastCheckTime, authState.isAuthenticated, setUserProfileFromSession]);

    // Fix 2: Re-implement the authentication status check in a safer way
    useEffect(() => {
        let isActive = true;

        const checkAuth = async () => {
            if (isActive) {
                await checkAuthStatus();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isActive) {
                checkAuth();
            }
        };

        checkAuth();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            isActive = false;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkAuthStatus]);

    // Axios interceptor for token refresh on 401 errors
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            response => response,
            async error => {
                const originalRequest = error.config;
                if ((error.response?.status === 401 || error.response?.status === 400) && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        const tokenCheck = await checkAndRefreshTokenIfNeeded();
                        if (tokenCheck.isValid) {
                            const newToken = sessionStorage.getItem('token');
                            if (newToken) {
                                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                                return axios(originalRequest);
                            }
                        }
                    } catch (refreshError) {
                        console.error("Token refresh failed:", refreshError);
                    }

                    await handleLogout();
                }
                return Promise.reject(error);
            }
        );

        return () => axios.interceptors.response.eject(interceptor);
    }, []);

    const handleLogout = useCallback(async () => {
        await logout();
        setAuthState({
            isAuthenticated: false,
            userProfile: {
                email: null,
                firstName: null,
                lastName: null,
                profileUrl: null,
                sub: null,
            }
        });
        window.location.href = `${process.env.REACT_APP_BASE_URL}`;
    }, []);

    const handleLogin = useCallback(() => {
        setUserProfileFromSession();
    }, [setUserProfileFromSession]);

    const autoLoginIfPossible = useCallback(async () => {
        setLoading(true);
        await checkAuthStatus();
        setLoading(false);
    }, [checkAuthStatus]);

    return {
        ...authState,
        loading,
        handleLogout,
        handleLogin,
        autoLoginIfPossible,
        updateUserCounters
    };
};